import React, { useMemo } from 'react';
import { ChevronDown, LucideIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem
} from '@epoch-ui/components/DropdownMenu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip';
import { GuestStatus } from 'types/guest.types';
import { Badge } from '@epoch-ui/components/Badge';
import { Button } from '@epoch-ui/components/Button';
import { cn } from '@epoch-ui/lib/utils';

import { useGuestSideSheetContext } from '../../guestSideSheetContext';
import { getIconForStatus } from '../../utils';

interface StatusDisplayData {
  text: string;
  className: string;
  icon?: JSX.Element;
}

const getDisplayDataForStatus = (status: GuestStatus): StatusDisplayData => {
  const StatusIcon: LucideIcon = getIconForStatus(status);
  const icon = <StatusIcon />;
  switch (status) {
    case GuestStatus.CONFIRMED:
      return {
        text: 'Going',
        className: 'tw-text-white tw-bg-rsvp-green',
        icon
      };
    case GuestStatus.WAITLISTED:
      return {
        text: 'Waitlisted',
        className: 'tw-text-white tw-bg-accent-purple',
        icon
      };
    case GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY:
      return {
        text: 'Incomplete',
        className: 'tw-text-black tw-bg-rsvp-yellow',
        icon
      };
    case GuestStatus.ACTION_REQUIRED:
      return {
        text: 'Pending',
        className: 'tw-text-black tw-bg-light-grey',
        icon
      };
    default:
      return {
        text: 'None',
        className: 'tw-text-black tw-bg-light-grey',
        icon
      };
  }
};

interface DropdownOption {
  status: GuestStatus;
  text: string;
  icon?: JSX.Element;
}

/**
 * Determines the available dropdown options for a plus-one guest based on:
 * - Plus-one's current status
 * - Event state (full/waitlisted or has spaces)
 * - Owner guest's status
 *
 * Returns empty array if dropdown should be disabled (show badge only)
 */
const getDropdownOptions = (
  currentStatus: GuestStatus,
  isEventWaitlisted: boolean,
  ownerStatus: GuestStatus
): DropdownOption[] => {
  const createOption = (status: GuestStatus): DropdownOption => {
    const displayData = getDisplayDataForStatus(status);
    return {
      status,
      text: displayData.text,
      icon: displayData.icon
    };
  };

  const disabledStatuses = [GuestStatus.ACTION_REQUIRED, GuestStatus.WAITLISTED];

  if (disabledStatuses.includes(currentStatus) && disabledStatuses.includes(ownerStatus)) {
    return [];
  }

  // Pending (ACTION_REQUIRED) + owner not waitlisted/pending + event full -> Confirmed, Waitlisted
  if (currentStatus === GuestStatus.ACTION_REQUIRED && isEventWaitlisted) {
    return [createOption(GuestStatus.CONFIRMED), createOption(GuestStatus.WAITLISTED)];
  }

  // Pending (ACTION_REQUIRED) + owner not waitlisted/pending + event has spaces -> Confirmed only
  if (currentStatus === GuestStatus.ACTION_REQUIRED && !isEventWaitlisted) {
    return [createOption(GuestStatus.CONFIRMED)];
  }

  // Going (CONFIRMED) + event full -> Going (current), Waitlisted
  if (currentStatus === GuestStatus.CONFIRMED && isEventWaitlisted) {
    return [createOption(GuestStatus.CONFIRMED), createOption(GuestStatus.WAITLISTED)];
  }

  // Going (CONFIRMED) + event has spaces -> No dropdown (badge only)
  if (currentStatus === GuestStatus.CONFIRMED && !isEventWaitlisted) {
    return [];
  }

  // Waitlisted + owner going -> Going, Waitlisted (current)
  if (
    currentStatus === GuestStatus.WAITLISTED &&
    [GuestStatus.CONFIRMED, GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY].includes(ownerStatus)
  ) {
    return [createOption(GuestStatus.CONFIRMED), createOption(GuestStatus.WAITLISTED)];
  }

  // Incomplete + event has spaces -> Going, Incomplete (current)
  if (currentStatus === GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY && !isEventWaitlisted) {
    return [
      createOption(GuestStatus.CONFIRMED),
      createOption(GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY)
    ];
  }

  // Incomplete + event is waitlisted -> Going, Waitlisted, Incomplete (current)
  if (currentStatus === GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY && isEventWaitlisted) {
    return [
      createOption(GuestStatus.CONFIRMED),
      createOption(GuestStatus.WAITLISTED),
      createOption(GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY)
    ];
  }

  return [];
};

export interface PlusOneRsvpControlDropdownProps {
  onStatusSelect: (targetStatus: GuestStatus) => void;
}

export const PlusOneRsvpControlDropdown: React.FC<PlusOneRsvpControlDropdownProps> = ({
  onStatusSelect
}) => {
  const { guestData, selectedPlusOneGuest, selectedSession } = useGuestSideSheetContext();
  const { guest: ownerGuest, event } = guestData;

  const isEventWaitlisted = selectedSession
    ? !!selectedSession.isEventWaitlisted
    : event.isEventWaitlisted;

  const { status: currentStatus } = selectedPlusOneGuest!;
  const { status: ownerStatus } = ownerGuest;

  const currentDisplayData = useMemo(() => getDisplayDataForStatus(currentStatus), [currentStatus]);

  const dropdownOptions = useMemo(
    () => getDropdownOptions(currentStatus, isEventWaitlisted, ownerStatus),
    [currentStatus, isEventWaitlisted, ownerStatus]
  );

  const isDropdownDisabled = dropdownOptions.length === 0;

  const handleSelect = (option: DropdownOption): void => {
    if (option.status !== currentStatus) {
      onStatusSelect(option.status);
    }
  };

  // Render as simple badge when dropdown is disabled
  if (isDropdownDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Badge className={cn(currentDisplayData.className, 'tw-rounded-xl tw-p-3 tw-text-sm')}>
              {currentDisplayData.text}
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="tw-max-w-[336px] tw-rounded-xl"
          side="right"
          align="end"
          alignOffset={40}
          sideOffset={-30}
        >
          {((): React.ReactNode => {
            switch (currentStatus) {
              case GuestStatus.CONFIRMED:
                return (
                  <>
                    <p className="tw-mb-2 tw-leading-4">
                      If this guest is no longer going, remove them from the event instead.
                    </p>
                    <a
                      href="https://help.epochapp.com/articles/plus-one-guests"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-font-medium tw-text-popover tw-underline"
                    >
                      Learn more
                    </a>
                  </>
                );
              case GuestStatus.WAITLISTED:
              case GuestStatus.ACTION_REQUIRED:
                return (
                  <p className="tw-leading-4">
                    {ownerGuest.firstName} is currently{' '}
                    {ownerStatus === GuestStatus.WAITLISTED ? 'waitlisted' : 'pending'} for this
                    event. You&apos;ll need to update {ownerGuest.firstName}&apos;s status to going
                    before you can change their plus one&apos;s status.
                  </p>
                );
              default:
                return null;
            }
          })()}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="xs" className={cn(currentDisplayData.className, 'tw-rounded-xl')}>
          <span>{currentDisplayData.text}</span>
          <ChevronDown className="tw-ml-1" size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {dropdownOptions.map((option) => (
          <DropdownMenuItem
            key={option.status}
            className={cn(option.status === currentStatus && 'tw-text-primary-blue')}
            onClick={(): void => handleSelect(option)}
          >
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 [&_svg]:tw-h-4 [&_svg]:tw-w-4">
              {option.icon}
              <div>{option.text}</div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
